import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { functions, storage } from 'firebaseSDK';
import convertObjFieldsToNumbers from '../../helpers/convertObjFieldsToNumbers';
import Actions from './abstract/Actions';
import { getFirestore, doc, Timestamp, setDoc } from 'firebase/firestore';

class PropertyActions extends Actions {
  async propertyPreSubmit_(data) {
    const propertyData = convertObjFieldsToNumbers(data, [
      'bedroomCount',
      'bathroomCount',
      'tenancyDepositAmount',
      'tenancyRentAmount',
    ]);

    propertyData.propertyUid =
      propertyData.propertyUid || uuidv4().toUpperCase();
    propertyData.dateUpdated = Timestamp.fromDate(new Date());
    propertyData.imageUrls = await Promise.all(
      propertyData.imageUrls.map((u) => {
        if (u.startsWith('data:')) {
          // Upload image
          const imageRef = ref(
            getStorage(),
            `propertyImages/${propertyData.propertyUid}/${uuidv4()}`
          );

          return uploadString(imageRef, u, 'data_url').then(() =>
            getDownloadURL(imageRef)
          );
        }

        return Promise.resolve(u);
      })
    );

    return propertyData;
  }

  async createProperty(data) {
    const { profile, publicAccount } = this.root.userDataState;
    const propertyData = await this.propertyPreSubmit_(data);

    propertyData.dateCreated = propertyData.dateUpdated;
    propertyData.landlordUid = profile.data.profileUid;
    propertyData.landlordEmail = profile.data.emailAddress;

    // Make property available for search
    propertyData.search.onTheMarket = false;
    propertyData.search.dateAvailable = propertyData.dateUpdated;

    propertyData.standards.eicrExpiresOn = propertyData.dateUpdated;
    propertyData.standards.epcExpiresOn = propertyData.dateUpdated;
    propertyData.standards.gscExpiresOn = propertyData.dateUpdated;

    await setDoc(
      doc(getFirestore(), `properties/${propertyData.propertyUid}`),
      propertyData
    );

    await publicAccount.update({
      landlordPropertyUids: [
        ...publicAccount.data.landlordPropertyUids,
        propertyData.propertyUid,
      ],
    });

    functions.epc.getEPCData({
      propertyUid: propertyData.propertyUid,
    });
  }

  async editProperty(propertyDoc, data) {
    return propertyDoc.update(await this.propertyPreSubmit_(data));
  }

  async inventoryPreSubmit_(data) {
    const inventoryData = convertObjFieldsToNumbers(data, ['itemCostAmount']);
    inventoryData.dateUpdated = Timestamp.fromDate(new Date());

    if (inventoryData.imageUrls?.length) {
      inventoryData.imageUrls = await Promise.all(
        inventoryData.imageUrls.map((u) => {
          if (u.startsWith('data:')) {
            // Upload image
            const ref = storage.getInventoryImage(
              inventoryData.propertyUid,
              inventoryData.itemUid
            );

            return uploadString(ref, u, 'data_url').then(() =>
              getDownloadURL(ref)
            );
          }

          return Promise.resolve(u);
        })
      );
    }

    return inventoryData;
  }

  async editInventory(propertyDoc, inventoryDoc, data) {
    data.itemUid = inventoryDoc.id; // We need the UID for image upload
    data.propertyUid = propertyDoc.id;

    return inventoryDoc.update(await this.inventoryPreSubmit_(data));
  }

  async createInventory(propertyDoc, data) {
    const { profile } = this.root.userDataState;
    data.itemUid = uuidv4().toUpperCase();
    data.propertyUid = propertyDoc.id;
    const inventoryData = await this.inventoryPreSubmit_(data);

    inventoryData.dateCreated = inventoryData.dateUpdated;
    inventoryData.landlordUid = profile.data.profileUid;

    console.log('Send ', inventoryData);

    return setDoc(
      doc(
        getFirestore(),
        `properties/${data.propertyUid}/inventoryItems/${data.itemUid}`
      ),
      inventoryData
    );
  }

  deleteProperty(propertyDoc) {
    const { publicAccount } = this.root.userDataState;
    const propertyUid = propertyDoc.id;
    const dateUpdated = Timestamp.fromDate(new Date());

    return propertyDoc
      .update({
        hasDeleted: true,
        dateUpdated,
      })
      .then(() => {
        // Do not return promise here
        publicAccount
          .update({
            landlordPropertyUids:
              publicAccount.data.landlordPropertyUids.filter(
                (uid) => uid !== propertyUid
              ),
            dateUpdated,
          })
          .catch((e) => console.warn('Failed to update landlordPropertyUids'));
      });
  }

  updateEPCUrl(propertyDoc, url) {
    return propertyDoc.update({
      standards: {
        ...propertyDoc.data.standards,
        epcUrl: url,
      },
      dateUpdated: Timestamp.fromDate(new Date()),
    });
  }

  updateEICR(propertyDoc, documentBase64, date) {
    return this.uploadStandardsDocument_(
      propertyDoc,
      'eicr',
      documentBase64,
      date
    );
  }

  updateGSC(propertyDoc, documentBase64, date, hasGasAppliances) {
    return this.uploadStandardsDocument_(
      propertyDoc,
      'gsc',
      documentBase64,
      date,
      {
        hasGasAppliances,
      }
    );
  }

  updateSmokeAlarms(propertyDoc, value = true) {
    return propertyDoc.update({
      standards: {
        ...propertyDoc.data.standards,
        smokeAlarmsChecked: value,
      },
      dateUpdated: Timestamp.fromDate(new Date()),
    });
  }

  async uploadStandardsDocument_(
    propertyDoc,
    name,
    documentBase64,
    date,
    additionalData = {}
  ) {
    const propertyUrl = `${name}Url`;
    const propertyDate = `${name}ExpiresOn`;

    const docRef = storage.getPropertyDocument(
      this.propertyUid,
      uuidv4().toUpperCase()
    );

    const standards = {
      ...propertyDoc.data.standards,
      ...additionalData,
    };

    standards[propertyUrl] = await uploadString(
      docRef,
      documentBase64,
      'data_url'
    ).then(() => getDownloadURL(docRef));

    standards[propertyDate] = Timestamp.fromDate(date);

    return propertyDoc.update({
      standards,
      dateUpdated: Timestamp.fromDate(new Date()),
    });
  }

  toggleSaved(propertyDoc) {
    const { publicAccount } = this.root.userDataState;
    const propertyUid =
      typeof propertyDoc === 'object' ? propertyDoc.id : propertyDoc;

    const newSaved = [...publicAccount.data.savedPropertyUids] || [];
    const idx = newSaved.indexOf(propertyUid);
    if (idx !== -1) {
      newSaved.splice(idx, 1);
    } else {
      newSaved.unshift(propertyUid);
    }

    return publicAccount.update({
      savedPropertyUids: newSaved,
    });
  }
}

export default PropertyActions;
